import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

import { isNavItemActive } from '../constants/navigationItems';
import { useNavUsage } from '../hooks/useNavUsage';
import { useVisibleNavItems } from '../hooks/useVisibleNavItems';
import { NavMoreMenu } from './NavMoreMenu';

const EXPANDED_WIDTH = 280;
const COLLAPSED_WIDTH = 76;

const Sidebar = ({
  collapsed = false,
  onToggleCollapsed = () => {},
  mobileVisible = true,
  onNavigate = () => {},
}) => {
  const location = useLocation();
  const { visibleItems } = useVisibleNavItems();
  const { frequentItems, unusedItems } = useNavUsage(visibleItems);

  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [flyoutGroupId, setFlyoutGroupId] = useState(null);
  const [flyoutRect, setFlyoutRect] = useState(null);
  const flyoutAnchorRef = useRef(null);
  const flyoutPanelRef = useRef(null);
  const sidebarContentRef = useRef(null);

  const isActive = useCallback((path) => isNavItemActive(path, location), [location]);

  const isGroupActive = useCallback(
    (item) =>
      item.children
        ? item.children.some((c) => isActive(c.path))
        : isActive(item.path),
    [isActive],
  );

  // Auto-expand any group whose child matches the current route
  useEffect(() => {
    setExpandedGroups((prev) => {
      let changed = false;
      const next = new Set(prev);
      visibleItems.forEach((item) => {
        if (item.children && item.children.some((c) => isActive(c.path))) {
          if (!next.has(item.id)) { next.add(item.id); changed = true; }
        }
      });
      return changed ? next : prev;
    });
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close flyout on outside click (the flyout is portaled to <body>, so check
  // both the trigger button and the portaled panel for containment)
  useEffect(() => {
    if (!flyoutGroupId) return undefined;
    const onDown = (e) => {
      const inAnchor = flyoutAnchorRef.current && flyoutAnchorRef.current.contains(e.target);
      const inPanel = flyoutPanelRef.current && flyoutPanelRef.current.contains(e.target);
      if (!inAnchor && !inPanel) {
        setFlyoutGroupId(null);
        setFlyoutRect(null);
      }
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [flyoutGroupId]);

  // Close flyout if the sidebar scrolls or the viewport resizes, since its
  // position is computed once from the trigger button's rect at open time
  useEffect(() => {
    if (!flyoutGroupId) return undefined;
    const close = () => { setFlyoutGroupId(null); setFlyoutRect(null); };
    const scrollEl = sidebarContentRef.current;
    scrollEl?.addEventListener('scroll', close);
    window.addEventListener('resize', close);
    return () => {
      scrollEl?.removeEventListener('scroll', close);
      window.removeEventListener('resize', close);
    };
  }, [flyoutGroupId]);

  const handleNavClick = useCallback(() => {
    setFlyoutGroupId(null);
    setFlyoutRect(null);
    onNavigate?.();
  }, [onNavigate]);

  const toggleGroup = (id) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // ── Collapsed sidebar: flyout panel shown on click ────────────────────────
  const renderCollapsedGroup = (item) => {
    const Icon = item.icon;
    const active = isGroupActive(item);
    const open = flyoutGroupId === item.id;
    const shortLabel = item.shortLabel || item.label.slice(0, 5);

    const handleToggle = (e) => {
      if (open) {
        setFlyoutGroupId(null);
        setFlyoutRect(null);
        return;
      }
      setFlyoutRect(e.currentTarget.getBoundingClientRect());
      setFlyoutGroupId(item.id);
    };

    return (
      <div key={item.id} className="nav-item mb-2 nav-collapsed-group">
        <button
          type="button"
          title={item.label}
          aria-expanded={open}
          aria-haspopup="menu"
          ref={open ? flyoutAnchorRef : null}
          className={`nav-link nav-link-collapsed btn border-0 d-flex flex-column align-items-center justify-content-center rounded-3 w-100 p-2${
            active ? ' nav-link--group-active' : ''
          }`}
          onClick={handleToggle}
        >
          <Icon size={20} />
          <span className="collapsed-short-label">{shortLabel}</span>
        </button>

        {open && flyoutRect && createPortal(
          <div
            className="nav-flyout"
            role="menu"
            aria-label={item.label}
            ref={flyoutPanelRef}
            style={{
              position: 'fixed',
              top: flyoutRect.top,
              left: flyoutRect.right + 8,
              maxHeight: `calc(100vh - ${flyoutRect.top}px - 16px)`,
              overflowY: 'auto',
            }}
          >
            <div className="nav-flyout__header">{item.label}</div>
            {item.children.map((child) => {
              const CIcon = child.icon;
              const childActive = isActive(child.path);
              return (
                <Link
                  key={child.id}
                  to={child.path}
                  role="menuitem"
                  className={`nav-flyout__item${childActive ? ' nav-flyout__item--active' : ''}`}
                  onClick={handleNavClick}
                >
                  <CIcon size={15} />
                  <span>{child.label}</span>
                </Link>
              );
            })}
          </div>,
          document.body,
        )}
      </div>
    );
  };

  // ── Expanded sidebar: accordion group ────────────────────────────────────
  const renderExpandedGroup = (item) => {
    const Icon = item.icon;
    const active = isGroupActive(item);
    const expanded = expandedGroups.has(item.id);

    return (
      <div key={item.id} className="nav-item mb-1">
        <button
          type="button"
          aria-expanded={expanded}
          className={`nav-link btn border-0 d-flex align-items-center gap-3 rounded-3 w-100 p-3${
            active && expanded
              ? ' nav-group-btn--active-open'
              : active
              ? ' bg-primary text-white'
              : ' text-dark hover-bg-light'
          }`}
          onClick={() => toggleGroup(item.id)}
        >
          <span className="icon-wrapper flex-shrink-0">
            <Icon size={20} />
          </span>
          <span className="flex-grow-1 text-start overflow-hidden">
            <span className="d-block fw-semibold text-truncate">{item.label}</span>
            <span className={`d-block small${active && !expanded ? ' text-white-50' : ' text-muted'}`}>
              {item.description}
            </span>
          </span>
          <ChevronDown
            size={16}
            className={`nav-group-chevron flex-shrink-0${expanded ? ' nav-group-chevron--open' : ''}`}
          />
        </button>

        {expanded && (
          <div className="nav-group-children">
            {item.children.map((child) => {
              const CIcon = child.icon;
              const childActive = isActive(child.path);
              return (
                <Link
                  key={child.id}
                  to={child.path}
                  className={`nav-child__link d-flex align-items-center gap-2 rounded-3 text-decoration-none px-3 py-2${
                    childActive ? ' nav-child__link--active' : ''
                  }`}
                  onClick={handleNavClick}
                >
                  <CIcon size={15} className="flex-shrink-0" />
                  <span className="text-truncate">{child.label}</span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ── Single leaf nav item ──────────────────────────────────────────────────
  const renderNavItem = (item, options = {}) => {
    if (item.children) {
      return collapsed ? renderCollapsedGroup(item) : renderExpandedGroup(item);
    }

    const { nested = false, compact = false } = options;
    const Icon = item.icon;
    const itemActive = isActive(item.path);
    const shortLabel =
      item.shortLabel || (item.label.length > 4 ? `${item.label.slice(0, 4)}…` : item.label);

    return (
      <div key={item.id} className={`nav-item mb-2${nested ? ' sidebar-ci-nested' : ''}`}>
        <Link
          to={item.path}
          title={item.label}
          aria-label={item.label}
          className={`nav-link d-flex align-items-center rounded-3 text-decoration-none${
            compact ? ' p-2' : ' p-3'
          }${itemActive ? ' bg-primary text-white' : ' text-dark hover-bg-light'}${
            collapsed ? ' nav-link-collapsed' : ' gap-3'
          }`}
          onClick={handleNavClick}
        >
          <span className={`icon-wrapper${collapsed ? ' d-flex justify-content-center w-100' : ''}`}>
            <Icon size={20} />
          </span>
          {collapsed ? (
            <span className="collapsed-label">
              <span className="collapsed-short-label">{shortLabel}</span>
            </span>
          ) : (
            <span className="flex-grow-1 overflow-hidden">
              <span className="d-block fw-semibold text-truncate">{item.label}</span>
              {!compact && (
                <span className={`d-block small${itemActive ? ' text-white-50' : ' text-muted'}`}>
                  {item.description}
                </span>
              )}
            </span>
          )}
        </Link>
      </div>
    );
  };

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <div
      className={`sidebar bg-white border-end${collapsed ? ' collapsed' : ''}${
        mobileVisible ? ' show' : ''
      }`}
      style={{
        width: collapsed ? `${COLLAPSED_WIDTH}px` : `${EXPANDED_WIDTH}px`,
        minWidth: collapsed ? `${COLLAPSED_WIDTH}px` : `${EXPANDED_WIDTH}px`,
        maxWidth: collapsed ? `${COLLAPSED_WIDTH}px` : `${EXPANDED_WIDTH}px`,
        flex: `0 0 ${collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH}px`,
        transition: 'width 0.25s ease, min-width 0.25s ease, max-width 0.25s ease',
      }}
    >
      {/* Header */}
      <div className="sidebar-header p-3 border-bottom">
        <div className="d-flex align-items-center justify-content-between">
          {!collapsed && <h6 className="mb-0 text-primary fw-bold">Navigation</h6>}
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => onToggleCollapsed?.(!collapsed)}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>
      </div>

      {/* Nav items */}
      <div className="sidebar-content p-3" ref={sidebarContentRef}>
        <nav className="nav flex-column">
          {frequentItems.length > 0 ? (
            <>
              {!collapsed && (
                <div className="sidebar-section-label text-muted small text-uppercase mb-2">
                  Frequent
                </div>
              )}
              {frequentItems.map((item) => renderNavItem(item))}
            </>
          ) : (
            !collapsed && (
              <p className="small text-muted mb-3 px-1">
                Open a page from More or the header — it will appear here sorted by use.
              </p>
            )
          )}

          {unusedItems.length > 0 && (
            <div className={`nav-item mb-2${frequentItems.length ? ' border-top pt-3 mt-1' : ''}`}>
              <NavMoreMenu
                items={unusedItems}
                location={location}
                onItemClick={handleNavClick}
                variant="sidebar"
                label="More"
              />
            </div>
          )}
        </nav>
      </div>

      {/* Footer */}
      {!collapsed && (
        <div className="sidebar-footer p-3 border-top mt-auto">
          <div className="text-center">
            <div className="mb-2">
              <img
                src="/logo.png"
                alt="AI Tech Assistant Logo"
                style={{ height: '30px', width: 'auto' }}
                className="d-inline-block"
              />
            </div>
            <div className="small text-muted">v1.0.0</div>
          </div>
        </div>
      )}

      <style>{`
        /* ── Base sidebar ──────────────────────────────────────── */
        .sidebar {
          position: sticky;
          top: 0;
          height: calc(100vh - 120px);
          overflow-y: auto;
          overflow-x: visible;
          z-index: 1000;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          box-shadow: 4px 0 18px rgba(15, 23, 42, 0.08);
          background: #fff;
        }
        .sidebar.collapsed { min-width: ${COLLAPSED_WIDTH}px; overflow-x: visible; }
        .sidebar-content { flex-grow: 1; overflow-x: visible; }
        .sidebar-section-label { letter-spacing: 0.06em; font-weight: 600; }
        .sidebar .nav-item { width: 100%; min-width: 0; }

        /* ── Leaf links ────────────────────────────────────────── */
        .sidebar .nav-link { transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease; }
        .sidebar .nav-link:hover { transform: translateX(2px); }
        .hover-bg-light:hover { background-color: #f8f9fa !important; }

        /* ── Collapsed leaf ────────────────────────────────────── */
        .nav-link-collapsed {
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.35rem;
          text-align: center;
          padding: 0.5rem 0.25rem !important;
        }
        .nav-link-collapsed .collapsed-label {
          width: 100%;
          min-width: 0;
          display: flex;
          justify-content: center;
        }
        .collapsed-short-label {
          display: block;
          width: 100%;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: 0.68rem;
          letter-spacing: 0.01em;
          line-height: 1.15;
          color: inherit;
        }

        /* ── Accordion group (expanded sidebar) ────────────────── */
        .nav-group-btn--active-open {
          background-color: rgba(13, 110, 253, 0.1) !important;
          color: #0d6efd !important;
        }
        .nav-group-chevron {
          transition: transform 0.2s ease;
          opacity: 0.55;
        }
        .nav-group-chevron--open { transform: rotate(180deg); }
        .nav-group-children {
          padding-left: 0.75rem;
          padding-bottom: 0.25rem;
          border-left: 2px solid #e2e8f0;
          margin-left: 1.25rem;
          margin-top: 0.15rem;
        }
        .nav-child__link {
          font-size: 0.875rem;
          color: #374151;
          font-weight: 500;
          transition: background 0.12s ease, color 0.12s ease;
          margin-bottom: 2px;
        }
        .nav-child__link:hover {
          background: #f1f5f9 !important;
          color: #0d6efd !important;
        }
        .nav-child__link--active {
          background: rgba(13, 110, 253, 0.1) !important;
          color: #0d6efd !important;
          font-weight: 600;
        }

        /* ── Collapsed group + flyout (portaled to <body>, positioned inline) ── */
        .nav-link--group-active {
          background-color: #0d6efd !important;
          color: #fff !important;
        }
        .nav-flyout {
          min-width: 210px;
          max-width: 260px;
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 0.5rem;
          box-shadow: 0 8px 28px rgba(15, 23, 42, 0.14);
          z-index: 1200;
        }
        .nav-flyout__header {
          padding: 0.5rem 0.85rem 0.35rem;
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.07em;
          text-transform: uppercase;
          color: #64748b;
          border-bottom: 1px solid #f1f5f9;
        }
        .nav-flyout__item {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          padding: 0.5rem 0.85rem;
          font-size: 0.875rem;
          font-weight: 500;
          color: #1e293b;
          text-decoration: none;
          transition: background 0.1s ease, color 0.1s ease;
        }
        .nav-flyout__item:hover { background: #f8fafc; color: #0d6efd; }
        .nav-flyout__item--active {
          background: rgba(13, 110, 253, 0.1);
          color: #0d6efd;
          font-weight: 600;
        }

        /* ── Mobile ────────────────────────────────────────────── */
        @media (max-width: 768px) {
          .sidebar {
            position: fixed;
            left: 0;
            top: 0;
            height: 100vh;
            transform: translateX(-100%);
            transition: transform 0.3s ease;
            overflow-x: visible;
          }
          .sidebar.show { transform: translateX(0); }
          .nav-flyout {
            left: calc(100% + 4px);
            max-width: min(260px, calc(100vw - ${COLLAPSED_WIDTH}px - 16px));
          }
        }
      `}</style>
    </div>
  );
};

export default Sidebar;
